import { z } from 'zod'

/**
 * Shared SEO sub-schema for content types carrying a `shared.seo` component block.
 *
 * Fields — `metaTitle`, `metaDescription`, `keywords`, `metaRobots`,
 * `canonicalURL`, `metaViewport`, `structuredData`, `metaImage`, `metaSocial`. Each is
 * `.nullish()` because the extracted YAML faithfully preserves the legacy CMS's
 * null/missing shape (e.g. `metaRobots:` with no value yields YAML null).
 *
 * The schema must stay JSON Schema-representable: Nuxt Content only stores a field as JSON
 * when its JSON Schema says object/array/anyOf. A `.transform()` converts to `{}`, which
 * Nuxt Content stores as `String(value)` — `[object Object]` — and every authored title
 * and description is lost. Normalize after retrieval with `normalizeSeo` instead.
 */

const metaSocialItemSchema = z.object({
    socialNetwork: z.string(),
    title: z.string().nullish(),
    description: z.string().nullish(),
}).passthrough()

const metaImageSchema = z.object({
    url: z.string(),
    width: z.number().nullish(),
    height: z.number().nullish(),
}).passthrough().nullish()

export const seoSchema = z.object({
    metaTitle: z.string().nullish(),
    metaDescription: z.string().nullish(),
    keywords: z.string().nullish(),
    metaRobots: z.string().nullish(),
    canonicalURL: z.string().nullish(),
    metaViewport: z.string().nullish(),
    structuredData: z.unknown().nullish(),
    metaImage: metaImageSchema,
    metaSocial: z.array(metaSocialItemSchema).nullish(),
}).nullish()

export type Seo = z.infer<typeof seoSchema>

/**
 * Collapses an SEO block with no populated field to `undefined`, so consumers'
 * `pageData?.seo || fallback` patterns reach the fallback. A block carrying only robots or
 * a canonical URL is kept. A non-object value means the stored data is broken; it is handed
 * to `onMalformed` so it gets reported rather than passed off as intentionally absent.
 */
export function normalizeSeo(seo: unknown, onMalformed?: (value: unknown) => void): NonNullable<Seo> | undefined {
    if (seo === null || seo === undefined) return undefined

    if (typeof seo !== 'object' || Array.isArray(seo)) {
        onMalformed?.(seo)
        return undefined
    }

    const v = seo as NonNullable<Seo>
    const empty
        = !v.metaTitle
            && !v.metaDescription
            && !v.keywords
            && !v.metaRobots
            && !v.canonicalURL
            && !v.metaViewport
            && !v.structuredData
            && !v.metaImage
            && (!v.metaSocial || v.metaSocial.length === 0)

    return empty ? undefined : v
}
